import { useEffect, useRef } from "react";
import type { Product } from "@/mocks/products";
import ProductCard from "./ProductCard";
import SortDropdown from "./SortDropdown";

interface ProductListingProps {
  title: string;
  totalCount: number;
  products: Product[];
  sortId: string;
  onSortChange: (id: string) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  onAddToCart: (product: Product) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
}

function SkeletonCard() {
  return (
    <div className="animate-pulse" aria-hidden="true">
      <div className="aspect-[4/5] w-full rounded-lg bg-background-100"></div>
      <div className="mt-3 h-3.5 w-4/5 rounded bg-background-100"></div>
      <div className="mt-2 h-4 w-2/5 rounded bg-background-100"></div>
      <div className="mt-3 h-10 w-full rounded-md bg-background-100"></div>
    </div>
  );
}

export default function ProductListing({
  title,
  totalCount,
  products,
  sortId,
  onSortChange,
  hasMore,
  onLoadMore,
  onAddToCart,
  hasActiveFilters,
  onClearFilters,
  loading = false,
  error = "",
  onRetry,
}: ProductListingProps) {
  const waiting = loading && products.length === 0;
  const failed = Boolean(error) && products.length === 0;
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore();
      },
      { rootMargin: "600px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  return (
    <div className="product-listing min-w-0 flex-1">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-heading text-3xl font-semibold text-foreground-950">{title}</h2>
          <p className="mt-1 text-sm text-foreground-500">
            {waiting ? "Carregando produtos..." : failed ? " " : `${totalCount} produtos encontrados`}
          </p>
        </div>
        <SortDropdown value={sortId} onChange={onSortChange} />
      </div>

      {hasActiveFilters && (
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={onClearFilters}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
          >
            <i className="ri-close-line text-sm"></i>
            Limpar filtros
          </button>
        </div>
      )}

      {waiting ? (
        <div className="product-grid mt-7 grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : failed ? (
        <div role="alert" className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 px-4 py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-primary-500">
            <i className="ri-wifi-off-line text-2xl"></i>
          </span>
          <p className="mt-4 text-sm text-foreground-600">{error}</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-background-50 hover:bg-primary-600"
              >
                Tentar novamente
              </button>
            )}
            <a href="/contato" className="text-sm text-primary-700 underline hover:text-primary-800">Fale conosco</a>
          </div>
        </div>
      ) : products.length === 0 ? (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-primary-500">
            <i className="ri-search-eye-line text-2xl"></i>
          </span>
          <p className="mt-4 text-sm text-foreground-600">
            Nenhum produto encontrado com esses filtros.
          </p>
          <button
            type="button"
            onClick={onClearFilters}
            className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-background-50 hover:bg-primary-600"
          >
            Limpar filtros
          </button>
        </div>
      ) : (
        <div className="product-grid mt-7 grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} />
          ))}
        </div>
      )}

      {hasMore && (
        <div ref={sentinelRef} className="mt-10 flex items-center justify-center py-6">
          <span className="flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-base"></i>
            Carregando mais produtos...
          </span>
        </div>
      )}
    </div>
  );
}
